import {
  ArrowSmallRightIcon,
  ArrowTopRightOnSquareIcon,
  TrashIcon
} from '@heroicons/react/24/outline'
import {
  MinusCircleIcon,
  PlusCircleIcon,
  Square3Stack3DIcon
} from '@heroicons/react/24/solid'
import { rankItem } from '@tanstack/match-sorter-utils'
import { useQueryClient } from '@tanstack/react-query'
import { format, formatDistance, subSeconds } from 'date-fns'
import { de, enUS } from 'date-fns/locale'
import { motion } from 'framer-motion'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { JSONTree } from 'react-json-tree'
import { Link, useNavigate } from 'react-router-dom'
import { Tooltip as ReactTooltip } from 'react-tooltip'

import { EmailVerificationRequiredModal, Seo } from '../components'
import ArchiveCareers from '../components/actions/archive-careers'
import ActivityIndicator from '../components/activity-indicator'
import UsageBanner from '../components/pricing/usage-banner'
import ColumnSettings from '../components/react-table/column-settings'
import IndeterminateCheckbox from '../components/react-table/indeterminate-checkbox'
import OptionsDropdown from '../components/react-table/options-dropdown'
import Table from '../components/react-table/table'
import SampleBadge from '../components/sample-data/sample-badge'
import SampleDataHint from '../components/sample-data/sample-data-hint'
import State from '../components/state'
import Button from '../components/tailwind/Button'
import PageHeading from '../components/tailwind/PageHeading'
import {
  useAivyContext,
  useLanguageContext,
  useNotificationContext,
  usePartnerContext,
  usePaymentContext,
  useUserContext
} from '../context'
import { useSampleDataDialog } from '../context/sample-data-context'
import { invalidatePricingUsage } from '../features/pricing/hooks'
import { classNames, toBase64, uuidSmall } from '../helpers'
import { trackEvent } from '../helpers/analytics'
import { filter } from '../helpers/filters'
import { localStorage } from '../helpers/local-storage'
import {
  SAMPLE_DATA_AVAILABILITY,
  isSampleCareer
} from '../helpers/sample-data'
import { sortByDate } from '../helpers/sortings'
import { TABLE_IDS, customizableMeta } from '../helpers/table-column-settings'
import { useCareersQuery } from '../hooks/use-careers-query'
import { useSampleDataAvailability } from '../hooks/use-sample-data'
import { useSetState } from '../hooks/use-set-state'
import { useTableColumnSettings } from '../hooks/use-table-column-settings'

const TRANSLATION_LOCATION = 'pages.careers.'
const locale = { de, en: enUS }

const container = {
  hidden: { opacity: 1, scale: 0 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { delayChildren: 0.3, staggerChildren: 0.2 }
  }
}

const item = {
  hidden: { y: 20, opacity: 0 },
  visible: { y: 0, opacity: 1 }
}

const prepareStatus = ({ status }) => {
  // if (archived) return 'ARCHIVED'

  switch (status) {
    case 'ACTIVE':
      return 'ACTIVE'
    case null:
    case 'WAIT_CULTURAL_FIT':
    case 'WAIT_EXPERTS':
    case 'WAIT_CONFIG':
    case 'WAIT_CONFIRM_TESTBATTERY':
    case 'WAIT_ACTIVE':
      return 'IN_PROGRESS'
    default:
      return 'IN_PROGRESS'
  }
}

function Careers() {
  const { t } = useTranslation()
  const { adminInsights } = useAivyContext()
  const { is_trial } = usePaymentContext()
  const { language } = useLanguageContext()
  const { cognitoUser } = useUserContext()
  const { alert } = useNotificationContext()
  const { partner } = usePartnerContext()
  const queryClient = useQueryClient()

  const { email_verified } = cognitoUser?.attributes || {}
  const isEmailVerified = email_verified === true || email_verified === 'true'
  const [
    isEmailVerificationRequiredModalVisible,
    setIsEmailVerificationRequiredModalVisible
  ] = useState(false)

  const [selectionState, setSelectionState] = useSetState({ careers: [] })
  const [displayArchiveCareers, setDisplayArchiveCareers] = useState(false)
  const [resetTableKey, setResetTableKey] = useState(uuidSmall())

  const navigate = useNavigate()

  const handleCreateCareerClick = () => {
    if (!isEmailVerified) {
      setIsEmailVerificationRequiredModalVisible(true)
      return
    }

    navigate('/career/new')
  }

  const {
    status,
    data: careers,
    refetch,
    dataUpdatedAt,
    fetchStatus
  } = useCareersQuery({
    select: (items) => {
      let result

      try {
        result = items
          .filter(({ id }) => !id.includes('PRODUCT_TOUR'))
          .filter(({ archived }) => !archived)
          .map((item) => ({
            ...item,
            status: prepareStatus(item)
          }))
      } catch (err) {
        console.error(err)
      }

      return result || []
    }
  })

  useEffect(() => {
    if (is_trial) return
    if (!careers) return
    if (!careers.find(({ status }) => status === 'ACTIVE')) return

    const storageSearchParams = localStorage.getItem('/careers')

    navigate(
      {
        search:
          storageSearchParams ||
          `filter=${toBase64([{ status: { in: ['ACTIVE'] } }])}`
      },
      { replace: true }
    )
  }, [is_trial, careers, navigate])

  const [time, setTime] = useState(null)

  const { openSampleDataDialog } = useSampleDataDialog()
  // Stellen liest der Hook selbst (inklusive archivierter Beispielstellen)
  const { availability, canAdd, canRemove } = useSampleDataAvailability()
  const canAddSampleData =
    canAdd && availability === SAMPLE_DATA_AVAILABILITY.NONE

  const fetchingSubtitle = useMemo(() => {
    if (status === 'error') return null

    // data updated at defaults to 0 for an empty cache
    if (!dataUpdatedAt) {
      return t('pages.careers.fetching_subtitle.is_loading')
    }

    if (fetchStatus === 'fetching') {
      return t('pages.careers.fetching_subtitle.is_updating')
    }

    return t('pages.careers.fetching_subtitle.last_updated_at', {
      time, // only used here for the use memo dependencies
      distance_sub_seconds: formatDistance(
        subSeconds(new Date(dataUpdatedAt), 3),
        new Date(),
        {
          locale: locale[language]
        }
      )
    })
  }, [status, dataUpdatedAt, fetchStatus, time, language, t])

  useEffect(() => {
    // update fetching subtitle every minute
    const interval = setInterval(() => setTime(Date.now()), 1000 * 60)

    return () => {
      clearInterval(interval)
    }
  }, [])

  const mapStatus = useCallback(
    ({ status }) => {
      switch (status) {
        case 'ACTIVE':
          return (
            <State
              color='green'
              text={t(TRANSLATION_LOCATION + 'state_active')}
            />
          )
        case 'IN_PROGRESS':
          return (
            <State
              color='orange'
              text={t(TRANSLATION_LOCATION + 'state_in_progress')}
            />
          )
        default:
          throw new Error(`map status failed - ${status} not found`)
      }
    },
    [t]
  )

  const mapAttractStatus = useCallback(
    ({ is_available_in_attract }) =>
      is_available_in_attract ? (
        <State
          color='green'
          text={t(TRANSLATION_LOCATION + 'state_attract_active')}
        />
      ) : (
        <State
          color='grey'
          text={t(TRANSLATION_LOCATION + 'state_attract_inactive')}
        />
      ),
    [t]
  )

  const handleNavigate = useCallback(
    (cell) => {
      const { id: career_id, status } = cell.row.original

      if (status === 'ACTIVE') {
        navigate(`/career/${career_id}`)
        return
      }

      navigate(`/career/new/${career_id}`)
    },
    [navigate]
  )

  const columns = useMemo(
    () =>
      [
        {
          id: 'select',
          enableHiding: false,
          disableOnClick: true,
          header: ({ table }) => (
            <IndeterminateCheckbox
              id='react-table-careers-select-all'
              checked={table.getIsAllRowsSelected()}
              onChange={table.getToggleAllRowsSelectedHandler()}
            />
          ),
          cell: ({ row }) => (
            <IndeterminateCheckbox
              checked={row.getIsSelected()}
              onChange={row.getToggleSelectedHandler()}
            />
          )
        },
        adminInsights && {
          id: 'admin-insights',
          accessorKey: 'id',
          enableSorting: false,
          enableHiding: false,
          disableOnClick: true,
          header: (
            <span className='text-sm font-semibold text-gray-900'>ID</span>
          ),
          cell: ({ getValue, row }) => (
            <>
              <span className='block text-sm font-medium text-gray-900'>
                {getValue()}
              </span>
              <JSONTree
                data={row.original}
                shouldExpandNodeInitially={() => false}
              />
            </>
          )
        },
        {
          accessorKey: 'title',
          sortingFn: 'alphanumeric',
          enableHiding: false,
          meta: {
            label: t(TRANSLATION_LOCATION + 'table_header_career_name'),
            primary: true
          },
          header: (
            <span className='text-sm font-semibold text-gray-900'>
              {t(
                TRANSLATION_LOCATION + 'table_header_career_name'
              ).toUpperCase()}
            </span>
          ),
          cell: ({ getValue, row }) => (
            <span className='inline-flex items-center gap-x-2'>
              <span className='text-sm font-medium text-gray-900'>
                {getValue()}
              </span>
              {isSampleCareer(row.original) && <SampleBadge kind='career' />}
            </span>
          )
        },
        !!careers?.find(({ external_custom_id }) => !!external_custom_id) && {
          accessorKey: 'external_custom_id',
          sortDescFirst: false,
          meta: customizableMeta(TABLE_IDS.CAREERS, 'external_custom_id', t),
          header: (
            <span className='text-sm font-semibold text-gray-900'>
              {t(
                TRANSLATION_LOCATION + 'table_header_career_external_id'
              ).toUpperCase()}
            </span>
          ),
          cell: ({ getValue }) => (
            <span className='text-sm text-gray-500'>{getValue() || 'n/a'}</span>
          )
        },
        {
          accessorKey: 'number_of_spaces',
          sortDescFirst: true,
          meta: customizableMeta(TABLE_IDS.CAREERS, 'number_of_spaces', t),
          header: (
            <span className='text-sm font-semibold text-gray-900'>
              {t(
                TRANSLATION_LOCATION + 'table_header_career_number_of_spaces'
              ).toUpperCase()}
            </span>
          ),
          cell: ({ getValue }) => (
            <span className='text-sm text-gray-500'>{getValue()}</span>
          )
        },
        {
          accessorKey: 'updatedAt',
          sortDescFirst: true,
          sortingFn: sortByDate,
          meta: customizableMeta(TABLE_IDS.CAREERS, 'updatedAt', t),
          header: (
            <span className='text-sm font-semibold text-gray-900'>
              {t(
                TRANSLATION_LOCATION + 'table_header_career_updated_at'
              ).toUpperCase()}
            </span>
          ),
          cell: ({ getValue }) => (
            <span className='text-sm text-gray-500'>
              {t(TRANSLATION_LOCATION + 'datetime', {
                datetime: format(new Date(getValue()), 'dd.MM.yyyy | HH:mm')
              })}
            </span>
          )
        },
        {
          accessorKey: 'createdAt',
          sortDescFirst: true,
          sortingFn: sortByDate,
          meta: customizableMeta(TABLE_IDS.CAREERS, 'createdAt', t),
          header: (
            <span className='text-sm font-semibold text-gray-900'>
              {t(
                TRANSLATION_LOCATION + 'table_header_career_created_at'
              ).toUpperCase()}
            </span>
          ),
          cell: ({ getValue }) => (
            <span className='text-sm text-gray-500'>
              {format(new Date(getValue()), 'dd.MM.yyyy')}
            </span>
          )
        },
        // {
        //   accessorKey: 'createdBy',
        //   sortDescFirst: true,
        //   header: (
        //     <span className='text-sm font-semibold text-gray-900'>
        //       {t(
        //         TRANSLATION_LOCATION + 'table_header_career_created_by'
        //       ).toUpperCase()}
        //     </span>
        //   ),
        //   cell: ({ getValue }) => (
        //     <div className='flex flex-row items-center'>
        //       <span className='inline-flex h-8 w-8 items-center justify-center rounded-full bg-gray-500'>
        //         <span className='text-sm font-medium leading-none text-white'>
        //           AR
        //         </span>
        //       </span>
        //       <span className='ml-2 text-sm text-gray-600'>Arbnor Raci</span>
        //     </div>
        //   )
        // },
        {
          accessorKey: 'status',
          enableSorting: false,
          meta: customizableMeta(TABLE_IDS.CAREERS, 'status', t),
          header: (
            <span className='text-sm font-semibold text-gray-900'>
              {t(
                TRANSLATION_LOCATION + 'table_header_career_status'
              ).toUpperCase()}
            </span>
          ),
          cell: ({ cell }) => <span>{mapStatus(cell.row.original)}</span>,
          filter: {
            id: 'status',
            filterKey: 'filter',
            operator: 'in',
            fn: filter,
            name: t(TRANSLATION_LOCATION + 'filter_title'),
            column: 'status',
            options: [
              {
                value: 'ACTIVE',
                label: t(TRANSLATION_LOCATION + 'filter_option_active_title')
              },
              {
                value: 'IN_PROGRESS',
                label: t(
                  TRANSLATION_LOCATION + 'filter_option_in_progress_title'
                )
              }
            ].filter((item) => item)
          }
        },
        {
          accessorKey: 'is_available_in_attract',
          enableSorting: false,
          meta: customizableMeta(
            TABLE_IDS.CAREERS,
            'is_available_in_attract',
            t
          ),
          header: (
            <span
              className='text-sm font-semibold text-gray-900'
              title={t(TRANSLATION_LOCATION + 'attract_column_description')}
            >
              {t(
                TRANSLATION_LOCATION +
                  'table_header_career_is_available_in_attract'
              ).toUpperCase()}
            </span>
          ),
          cell: ({ cell }) => {
            const { id } = cell.row.original

            return (
              <div className='flex items-center gap-2'>
                {mapAttractStatus(cell.row.original)}
                <Link
                  to={`/career/${id}#attract`}
                  onClick={(event) => event.stopPropagation()}
                  title={t(TRANSLATION_LOCATION + 'attract_link_action')}
                  aria-label={t(TRANSLATION_LOCATION + 'attract_link_action')}
                  className='text-gray-400 hover:text-gray-600'
                >
                  <ArrowTopRightOnSquareIcon className='h-4 w-4' />
                </Link>
              </div>
            )
          }
        },
        {
          accessorKey: 'id',
          header: null,
          cell: () => <div />,
          invisible: true,
          enableHiding: false
        },
        {
          accessorKey: 'view',
          enableSorting: false,
          enableHiding: false,
          header: '',
          cell: () => (
            <ArrowSmallRightIcon className='ml-auto h-5 w-5 text-gray-500 group-hover:text-gray-900' />
          )
        }
      ]
        .filter((item) => item)
        .map((item) => ({
          ...item,
          onClick: item.disableOnClick
            ? undefined
            : (cell) => handleNavigate(cell)
        })),
    [careers, mapStatus, mapAttractStatus, handleNavigate, adminInsights, t]
  )

  const columnSettings = useTableColumnSettings({
    tableId: TABLE_IDS.CAREERS,
    columns
  })

  const globalFilterFn = useCallback((row, columnId, value, addMeta) => {
    const columnValue = row.getValue(columnId)
    const itemRank = rankItem(columnValue, value)

    addMeta({ itemRank })

    return itemRank.passed
  }, [])

  const selectionCallback = useCallback(
    (table) => {
      setSelectionState({
        careers: table
          .getSelectedRowModel()
          .rows.map(({ original, toggleSelected }) => ({
            ...original,
            removeSelected: () => toggleSelected(false)
          }))
      })
    },
    [setSelectionState]
  )

  const dataUpdatedAtIsPastFiveMinutes = useMemo(
    () =>
      time &&
      dataUpdatedAt &&
      Math.floor((new Date() - new Date(dataUpdatedAt)) / 60000) >= 5 &&
      fetchStatus !== 'fetching',
    [dataUpdatedAt, fetchStatus, time]
  )

  if (status === 'pending') {
    return <ActivityIndicator />
  }

  const optionsDropdown = [
    canAddSampleData && {
      key: 'add-sample-data',
      label: t('sample_data.add.menu'),
      available: true,
      Icon: PlusCircleIcon,
      onClick: () =>
        openSampleDataDialog({ mode: 'add', source: 'careers_menu' })
    },
    availability === SAMPLE_DATA_AVAILABILITY.PRESENT && {
      key: 'remove-sample-data',
      label: t('sample_data.remove.menu'),
      available: true,
      Icon: MinusCircleIcon,
      onClick: () =>
        openSampleDataDialog({ mode: 'remove', source: 'careers_menu' })
    },
    {
      key: 'recover-careers',
      label: t('careers.options.recover_careers_title'),
      available: true,
      Icon: Square3Stack3DIcon,
      onClick: () => navigate('/recover/careers')
    }
  ].filter(Boolean)

  const pageHeadingParams = {
    title: t('seo.titles.careers'),
    subtitle: fetchingSubtitle,
    withRefetch: dataUpdatedAtIsPastFiveMinutes ? refetch : undefined,
    withActions: (
      <div className='flex flex-wrap items-center gap-x-2'>
        <Button.PrimaryXL
          id='btn-new-career'
          text={t(TRANSLATION_LOCATION + 'create_career_action')}
          onClick={handleCreateCareerClick}
        />

        {!careers.length && (
          <OptionsDropdown
            menuButtonSize='size-10'
            menuButtonIconSize='size-7'
            options={optionsDropdown}
          />
        )}
      </div>
    )
  }

  if (!careers.length) {
    return (
      <>
        <Seo title={t('seo.titles.careers')} />
        <PageHeading {...pageHeadingParams} />
        {/* z. B. nur archivierte Beispielstellen */}
        <SampleDataHint source='hint_careers' />
        <div className='mt-16 flex flex-col items-center'>
          <div className='text-center'>
            <span className='mb-1 block text-3xl font-extrabold tracking-tight text-gray-900'>
              {t(TRANSLATION_LOCATION + 'empty_title_1')}
            </span>
            <span className='border-b-4 border-yellow-200 px-2 text-lg font-bold text-gray-900'>
              {t(TRANSLATION_LOCATION + 'empty_title_2')}
            </span>
          </div>
          {canAddSampleData && (
            // eigener Button statt Button.Text, der Text muss auf Mobile umbrechen
            <button
              type='button'
              className='mt-8 px-4 text-center text-sm font-semibold text-[#1660F3] hover:text-primaryBlue/70'
              onClick={() =>
                openSampleDataDialog({ mode: 'add', source: 'careers_empty' })
              }
            >
              {t('sample_data.add.empty_link')}
            </button>
          )}
          <div className='hidden max-w-3xl'>
            <video
              className='mt-8 aspect-video w-full p-4'
              controls
              onPlaying={() => trackEvent('welcome_video_playing')}
              src='https://fra1.do.dubbcdn.com/videos/2022-07-20/fab3ba342fda7387ea9c619ccadaf6e0/720p_Dbxh.mp4'
            ></video>
          </div>
        </div>

        <EmailVerificationRequiredModal
          open={isEmailVerificationRequiredModalVisible}
          setOpen={setIsEmailVerificationRequiredModalVisible}
          title={t(
            TRANSLATION_LOCATION + 'email_verification_required_modal.title'
          )}
          description={t(
            TRANSLATION_LOCATION +
              'email_verification_required_modal.description'
          )}
          closeText={t(
            TRANSLATION_LOCATION +
              'email_verification_required_modal.close_action'
          )}
          verifyText={t(
            TRANSLATION_LOCATION +
              'email_verification_required_modal.verify_action'
          )}
          onVerify={() => navigate('/confirm-email')}
        />
      </>
    )
  }

  return (
    <>
      <Seo title={t('seo.titles.careers')} />
      <PageHeading {...pageHeadingParams} />
      {/* Nur in enforced ab 80 %, „Neue Stelle“ bleibt trotzdem frei, ein
          Entwurf geht immer */}
      <UsageBanner
        resource='careers'
        source='careers_page'
        className='px-4 pt-4 md:px-6 lg:px-8'
      />
      <SampleDataHint source='hint_careers' />

      <Table
        key={resetTableKey}
        data={careers}
        columns={columns}
        columnState={columnSettings.columnState}
        initialState={{ sorting: [{ id: 'updatedAt', desc: true }] }}
        globalFilterFn={globalFilterFn}
        selectionCallback={selectionCallback}
        withActions={(() => {
          const available =
            selectionState.careers.length > 0 &&
            selectionState.careers.every((c) => c.status === 'ACTIVE')

          return (
            <motion.div
              variants={container}
              initial='hidden'
              animate='visible'
              className='mr-8 flex items-center gap-x-2'
            >
              <div>
                <motion.button
                  id='action-archive-careers'
                  data-tooltip-id='action-archive-careers'
                  className={classNames(
                    'group flex h-8 items-center justify-center rounded-full px-1',
                    'cursor-pointer hover:bg-blue-700'
                  )}
                  onClick={
                    available
                      ? () => setDisplayArchiveCareers(true)
                      : () =>
                          alert(
                            t('careers.archive_bulk.not_available_title'),
                            [t('careers.archive_bulk.not_available_hint')],
                            [
                              {
                                text: t(
                                  'careers.archive_bulk.not_available_close'
                                ),
                                style: 'cancel'
                              }
                            ]
                          )
                  }
                  variants={item}
                >
                  <TrashIcon
                    className={classNames(
                      'h-6 w-6',
                      available ? 'text-white' : 'text-blue-400'
                    )}
                  />
                </motion.button>
                <ReactTooltip
                  id='action-archive-careers'
                  className='rounded-full font-medium'
                  content={t('careers.archive_bulk.tooltip')}
                  style={{ backgroundColor: '#182033' }}
                  place='top'
                  variant='info'
                />
              </div>
            </motion.div>
          )
        })()}
        disableVirtual={is_trial}
        fillHeight
        label={t('seo.titles.careers')}
        displayNumberOfEntries
        withOptions={
          <>
            <ColumnSettings
              tableId={TABLE_IDS.CAREERS}
              settings={columnSettings}
            />
            <OptionsDropdown options={optionsDropdown} />
          </>
        }
      />

      <EmailVerificationRequiredModal
        open={isEmailVerificationRequiredModalVisible}
        setOpen={setIsEmailVerificationRequiredModalVisible}
        title={t(
          TRANSLATION_LOCATION + 'email_verification_required_modal.title'
        )}
        description={t(
          TRANSLATION_LOCATION + 'email_verification_required_modal.description'
        )}
        closeText={t(
          TRANSLATION_LOCATION +
            'email_verification_required_modal.close_action'
        )}
        verifyText={t(
          TRANSLATION_LOCATION +
            'email_verification_required_modal.verify_action'
        )}
        onVerify={() => navigate('/confirm-email')}
      />
      <ArchiveCareers
        careers={selectionState.careers}
        canDelete={canRemove}
        updateHandler={() => {
          // Archivieren gibt den Platz sofort frei
          invalidatePricingUsage({
            queryClient,
            partnerId: partner?.id,
            resource: 'careers'
          })
          refetch()
          setResetTableKey(uuidSmall())
        }}
        open={displayArchiveCareers}
        setOpen={setDisplayArchiveCareers}
      />
    </>
  )
}

export default Careers
